import { Component, State, h } from '@stencil/core';
import { getReview } from '@ugc-api/review';
import { getReviewImagesDataByPageNumber, toSingleReviewInfo } from '@ugc-components/ugc-pdp-review/utils';
import state, { store } from '@ugc-store';
import { debounce, handleErrorMessage, isRtl } from '@ugc-utils/utils';
import { modalZIndex } from '@ugc-utils/modal-z-index';
import { Arrow } from '../../../icons';

@Component({
  tag: 'ugc-review-dt-all-images-detail-modal',
  styleUrl: 'ugc-review-dt-all-images-detail-modal.scss',
  shadow: false,
})
export class UgcReviewDtAllImagesDetailModal {
  @State() activeImageUrl: string = '';
  @State() maxScrollWidth: number;
  @State() pageNumberList = [];
  @State() reviewImages = [];
  @State() isLoading = false;
  @State() isImageLoading = false;
  @State() imageCount = 7;
  swipperElement!: HTMLDivElement;
  imageWidth = 104;
  gapWidth = 4;
  cellWidth = 0;
  moveDistance = 0;
  timer: number;

  constructor() {
    window.onresize = debounce(this.onSizeChange, 1000);
  }
  onSizeChange = () => {
    this.imageCount = window.innerWidth > 1024 ? 7 : 5;
    this.moveDistance = this.cellWidth * this.imageCount;
  };

  componentWillLoad() {
    store.onChange('isShowCustomerAllImagesDetailModal', data => {
      if (data) {
        const {
          dtAllCustomerImagesData,
          dtAllImagesPagination: { current },
          activeImageAssetId,
        } = state;
        this.cellWidth = this.imageWidth + this.gapWidth;
        this.moveDistance = this.cellWidth * this.imageCount;
        const imageList = dtAllCustomerImagesData[current];
        if (imageList && imageList.length) {
          this.pageNumberList = [current];
          this.reviewImages = imageList;
          this.setMaxScrollWidth(imageList);
          this.activeImageUrl = imageList.find(item => item.assetId === activeImageAssetId).paths.l;
          this.timer = window.setTimeout(() => {
            this.scrollToActveImage();
          }, 1000);
        }
      }
    });
  }

  disconnectedCallback() {
    this.timer = null;
    window.clearTimeout(this.timer);
  }

  translateXTo = (translateX: number) => {
    if (this.swipperElement) {
      this.swipperElement.style.transition = '0.5s';
      this.swipperElement.style.transform = `translateX(${isRtl() ? translateX : -translateX}px)`;
    }
  };

  getTranslateXDistance = () => {
    const lastMatrix = new DOMMatrixReadOnly(this.swipperElement.style.transform);
    return Math.abs(lastMatrix.m41);
  };

  getMinPage = () => {
    return Math.min(...this.pageNumberList);
  };

  getMaxPage = () => {
    return Math.max(...this.pageNumberList);
  };

  setMaxScrollWidth = imageList => {
    this.maxScrollWidth = Math.max(this.cellWidth * (imageList.length - this.imageCount), 0);
  };

  async getReviewDataById(reviewId) {
    if (this.isLoading) return;
    try {
      this.isLoading = true;
      const data = (await getReview(reviewId)) || {};
      this.isLoading = false;
      state.customerImagesDetailModalData = toSingleReviewInfo(data);
    } catch (e) {
      this.isLoading = false;
      handleErrorMessage(e);
    }
  }

  getMoreReviewImages = async (pageNumber, flag) => {
    if (this.isImageLoading) return;
    this.isImageLoading = true;
    const imageItems = await getReviewImagesDataByPageNumber(pageNumber);
    this.isImageLoading = false;
    this.pageNumberList = [...this.pageNumberList, pageNumber];
    this.reviewImages = flag === 'prev' ? [...imageItems, ...this.reviewImages] : [...this.reviewImages, ...imageItems];
    this.setMaxScrollWidth(this.reviewImages);
    return imageItems;
  };

  isHidePrevOrNextButton = () => {
    const {
      dtAllImagesPagination: { pages },
      activeImageAssetId,
    } = state;
    let isHidePrevButton = false;
    let isHideNextButton = false;
    const index = this.reviewImages.findIndex(item => item.assetId === activeImageAssetId);
    if (index === 0) {
      const minPage = this.getMinPage();
      if (minPage === 1) {
        isHidePrevButton = true;
      }
    }
    if (index === this.reviewImages.length - 1) {
      const maxPage = this.getMaxPage();
      if (maxPage === pages) {
        isHideNextButton = true;
      }
    }
    return {
      isHidePrevButton,
      isHideNextButton,
    };
  };
  handleClickPrev = async () => {
    const { activeImageAssetId } = state;
    const currentImageIndex = this.reviewImages.findIndex(item => item.assetId === activeImageAssetId);
    const minPage = this.getMinPage();
    if (currentImageIndex === 0) {
      if (minPage > 1) {
        const prevPageNumber = minPage - 1;
        // no prev images data
        const imageItems = await this.getMoreReviewImages(prevPageNumber, 'prev');
        const { reviewId, assetId, paths: { l = '' } = {} } = imageItems[imageItems.length - 1];
        this.onChangeSwipperActiveImage({
          reviewId,
          assetId,
          imageUrl: l,
        });
      }
    } else {
      const { assetId, reviewId, paths: { l = '' } = {} } = this.reviewImages[currentImageIndex - 1];
      this.onChangeSwipperActiveImage({
        reviewId,
        assetId,
        imageUrl: l,
      });
    }
  };

  handleClickNext = async () => {
    const {
      dtAllImagesPagination: { pages },
      activeImageAssetId,
    } = state;
    const currentImageIndex = this.reviewImages.findIndex(item => item.assetId === activeImageAssetId);
    const maxPage = this.getMaxPage();
    if (currentImageIndex === this.reviewImages.length - 1) {
      if (maxPage < pages) {
        const nextPageNumber = maxPage + 1;
        const imageItems = await this.getMoreReviewImages(nextPageNumber, 'next');
        const { reviewId, assetId, paths: { l = '' } = {} } = imageItems[0];
        this.onChangeSwipperActiveImage({
          reviewId,
          assetId,
          imageUrl: l,
        });
      }
    } else {
      const { assetId, reviewId, paths: { l = '' } = {} } = this.reviewImages[currentImageIndex + 1];
      this.onChangeSwipperActiveImage({
        reviewId,
        assetId,
        imageUrl: l,
      });
    }
  };

  handlePrevMove = async () => {
    const translateXDistance = this.getTranslateXDistance();
    const minPage = this.getMinPage();
    if (translateXDistance === 0 && minPage > 1) {
      const prevPageNumber = minPage - 1;
      await this.getMoreReviewImages(prevPageNumber, 'prev');
    }
    this.translateXTo(Math.max(translateXDistance - this.moveDistance, 0));
  };
  handleNextMove = async () => {
    const {
      dtAllImagesPagination: { pages },
    } = state;
    const translateXDistance = this.getTranslateXDistance();
    const maxPage = this.getMaxPage();

    if (this.maxScrollWidth - translateXDistance === 0 && maxPage < pages) {
      const nextPageNumber = maxPage + 1;
      await this.getMoreReviewImages(nextPageNumber, 'next');
    }
    this.translateXTo(Math.min(translateXDistance + this.moveDistance, this.maxScrollWidth));
  };
  onChangeSwipperActiveImage = ({ reviewId, assetId, imageUrl }) => {
    const { activeImageReviewId } = state;
    if (activeImageReviewId !== reviewId) {
      this.getReviewDataById(reviewId);
    }
    state.activeImageAssetId = assetId;
    state.activeImageReviewId = reviewId;
    this.activeImageUrl = imageUrl;
    this.scrollToActveImage();
  };
  scrollToActveImage = () => {
    const index = this.reviewImages.findIndex(item => item.assetId === state.activeImageAssetId);
    const translateX = Math.max(0, this.cellWidth * (index + 1 - Math.ceil(this.imageCount / 2)));
    this.translateXTo(Math.min(translateX, this.maxScrollWidth));
  };
  handleCancel = () => {
    this.activeImageUrl = '';
    this.maxScrollWidth = 0;
    this.pageNumberList = [];
    this.reviewImages = [];
    state.isShowCustomerAllImagesDetailModal = false;
  };

  render() {
    const {
      reviewStaticLanguage: { IDS_LBL_REVIEW_IMAGES = '' },
      customerImagesDetailModalData,
      activeImageAssetId,
      isShowCustomerAllImagesDetailModal,
    } = state;

    const { isHidePrevButton, isHideNextButton } = this.isHidePrevOrNextButton();

    const renderBitImagePrevButton = !isHidePrevButton && (
      <div class="nav-button button-prev flex-row-center-center pointer" onClick={this.handleClickPrev}>
        <Arrow width="20" height="20" fill="#fff" />
      </div>
    );
    const renderBitImageNextButton = !isHideNextButton && (
      <div class="nav-button button-next flex-row-center-center pointer" onClick={this.handleClickNext}>
        <Arrow width="20" height="20" fill="#fff" />
      </div>
    );
    const renderBigImage = (
      <div class={`image-swiper-wrap flex-row-center-center ${this.isLoading ? 'active' : ''}`}>
        {<img src={this.activeImageUrl} />}
      </div>
    );

    const renderBigImageSwiper = (
      <div class="big-image-wrap">
        {renderBitImagePrevButton}
        {renderBigImage}
        {renderBitImageNextButton}
      </div>
    );

    const renderSwipperImageListWrap = (
      <div class="swipper-image-list-box">
        <div class="swipper-image-list flex-row-start-center gap-4" ref={el => (this.swipperElement = el)}>
          {this.reviewImages.map(({ reviewId, assetId, paths: { m = '', l = '' } = {} }) => (
            <div
              class="swipper-image-box flex-row-center-center pointer"
              onClick={this.onChangeSwipperActiveImage.bind(this, { reviewId, assetId, imageUrl: l })}
            >
              {m && <img src={m} />}
              {activeImageAssetId === assetId && <div class="active"></div>}
            </div>
          ))}
        </div>
      </div>
    );

    const renderPrevButton = this.reviewImages.length > this.imageCount && (
      <div class="nav-button button-prev flex-row-center-center pointer" onClick={this.handlePrevMove.bind(this)}>
        <Arrow width="20" height="20" fill="#fff" />
      </div>
    );
    const renderNextButton = this.reviewImages.length > this.imageCount && (
      <div class="nav-button button-next flex-row-center-center pointer" onClick={this.handleNextMove.bind(this)}>
        <Arrow width="20" height="20" fill="#fff" />
      </div>
    );

    return (
      <ugc-modal-dialog
        isShow={isShowCustomerAllImagesDetailModal}
        modalTitle={IDS_LBL_REVIEW_IMAGES}
        zIndex={modalZIndex['ugc-review-all-images-detail-modal']}
        rootStyle={{ width: '800px' }}
        handleCancel={this.handleCancel.bind(this)}
      >
        {isShowCustomerAllImagesDetailModal && (
          <div class="ugc-review-dt-images-detail-modal px-32 pb-24">
            <div class="flex-row-between-start ugc-customer-images-detail-content-wrap gap-24">
              {renderBigImageSwiper}
              <div class="image-detail-review-info-wrap">
                <ugc-review-item
                  key={customerImagesDetailModalData.id}
                  reviewItem={customerImagesDetailModalData}
                  isModal={true}
                  handleClickImage={this.onChangeSwipperActiveImage}
                />
              </div>

              <div class={`ugc-customer-images-detail-content-mantle ${this.isLoading ? 'active' : ''}`} />
            </div>
            <div class="mt-24 relative flex-row-center-center">
              {renderPrevButton}
              {renderSwipperImageListWrap}
              {renderNextButton}
            </div>
          </div>
        )}
      </ugc-modal-dialog>
    );
  }
}
